import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { useDashboardPriceDrops } from "@/api/dashboard";

/**
 * Price drops in the last 7 days, newest first (MPPI-00653).
 *
 * A table rather than a list, because "how long ago" is a column the operator
 * sorts by: a 12% cut yesterday and a 12% cut six days ago are different
 * news, and the old layout buried the date inside a run-on detail line.
 *
 * Sized to match `DistressedSignalsPanel` beside it and scrolled internally,
 * so the two sit level on the dashboard however many rows each holds.
 */
const fmtAed = (n: number | null | undefined) =>
  n != null ? `AED ${Math.round(n).toLocaleString()}` : "—";

/** Whole days between a drop and now. Null dates sort last, never as zero. */
function daysAgo(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  return Math.max(0, Math.floor((Date.now() - then) / 86_400_000));
}

function agoLabel(days: number | null): string {
  if (days == null) return "—";
  if (days === 0) return "Today";
  if (days === 1) return "1 day";
  return `${days} days`;
}

type SortKey = "ago" | "drop" | "price";

export function PriceDropsPanel() {
  // 20 rows, matching what the operator asked to be able to scroll to. The
  // API is asked for exactly that many rather than for a page we then crop.
  const { data, isLoading } = useDashboardPriceDrops(7, 20);
  const [sort, setSort] = useState<SortKey>("ago");

  const rows = useMemo(() => {
    const list = (data ?? []).map((d) => ({ ...d, ago: daysAgo(d.last_price_change_date) }));
    return list.sort((a, b) => {
      if (sort === "ago") {
        // Undated rows last whichever way the column is read.
        if (a.ago == null) return 1;
        if (b.ago == null) return -1;
        return a.ago - b.ago;
      }
      if (sort === "drop") return (a.change_pct ?? 0) - (b.change_pct ?? 0);
      return (b.current_price_aed ?? 0) - (a.current_price_aed ?? 0);
    });
  }, [data, sort]);

  const th = (key: SortKey, label: string, align = "text-left") => (
    <th className={`py-1 ${align}`}>
      <button
        type="button"
        onClick={() => setSort(key)}
        className={`uppercase tracking-wide ${
          sort === key ? "font-semibold text-gray-700 underline" : "text-gray-500"
        }`}
      >
        {label}
      </button>
    </th>
  );

  return (
    <div className="flex h-[26rem] flex-col rounded-lg border bg-white p-4">
      <div className="mb-2 flex items-baseline justify-between">
        <h3 className="text-sm font-semibold text-gray-800">Price Drops · 7d</h3>
        <span className="text-[10px] text-gray-500">
          vs first-seen price · secondary &amp; ready
        </span>
      </div>
      {isLoading && <p className="py-6 text-center text-xs text-gray-400">Loading…</p>}
      {!isLoading && rows.length === 0 && (
        <p className="py-6 text-center text-xs text-gray-400">
          No price drops yet — listings need a fresh data refresh to detect changes.
        </p>
      )}
      {rows.length > 0 && (
        <div className="min-h-0 flex-1 overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-white text-[10px] text-gray-500">
              <tr>
                <th className="py-1 text-left uppercase tracking-wide">Listing</th>
                {th("ago", "Dropped", "text-right")}
                {th("drop", "Change", "text-right")}
                {th("price", "Price", "text-right")}
              </tr>
            </thead>
            <tbody className="divide-y">
              {rows.map((d) => {
                const where = [d.community, d.emirate]
                  .filter((s): s is string => Boolean(s && s.trim()))
                  .join(" · ");
                const name = (
                  <>
                    <p className="truncate font-medium text-gray-900">
                      {d.title || where || "Listing"}
                    </p>
                    {where && <p className="truncate text-[10px] text-gray-500">{where}</p>}
                  </>
                );
                return (
                  <tr key={d.listing_id} className="align-top hover:bg-gray-50">
                    <td className="max-w-0 py-1.5 pr-2">
                      {d.property_id ? (
                        <Link to={`/properties/${d.property_id}`} className="block">
                          {name}
                        </Link>
                      ) : (
                        name
                      )}
                    </td>
                    <td className="whitespace-nowrap py-1.5 text-right tabular-nums text-gray-600">
                      {agoLabel(d.ago)}
                    </td>
                    <td
                      className={`whitespace-nowrap py-1.5 text-right tabular-nums ${
                        d.change_pct != null && d.change_pct < 0
                          ? "font-semibold text-emerald-600"
                          : "text-gray-500"
                      }`}
                    >
                      {d.change_pct != null ? `${d.change_pct.toFixed(1)}%` : "—"}
                    </td>
                    <td className="whitespace-nowrap py-1.5 text-right font-semibold tabular-nums text-gray-900">
                      {fmtAed(d.current_price_aed)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
